import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { fr as frLocale, enGB } from 'date-fns/locale';
import { Calendar } from '../components/ui/calendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const TXT = {
  fr: {
    title: 'Votre rendez-vous', with: 'Échange confidentiel avec Nidale Barret', formats: { call: 'Appel téléphonique', video: 'Visioconférence', meeting: 'Rencontre à Paris' },
    move: 'Déplacer le rendez-vous', cancel: 'Annuler le rendez-vous', pick: 'Choisissez un nouveau créneau', paris: 'Horaires à l\'heure de Paris',
    confirmMove: 'Confirmer le nouveau créneau', back: 'Retour', confirmCancel: 'Voulez-vous vraiment annuler ce rendez-vous ?',
    moved: 'Votre rendez-vous a été déplacé. Un e-mail de confirmation vous a été envoyé.', cancelled: 'Votre rendez-vous a bien été annulé.',
    taken: 'Ce créneau vient d\'être réservé. Merci d\'en choisir un autre.', error: 'Une erreur est survenue. Veuillez réessayer.',
    notFound: 'Ce rendez-vous est introuvable ou a déjà été annulé.', past: 'Ce rendez-vous est déjà passé.', newBooking: 'Prendre un nouveau rendez-vous',
    doneCancel: 'Rendez-vous annulé. Nous serons heureux d\'échanger avec vous quand vous le souhaiterez.', at: 'à', place: 'Lieu'
  },
  en: {
    title: 'Your appointment', with: 'Private conversation with Nidale Barret', formats: { call: 'Phone call', video: 'Video call', meeting: 'Meeting in Paris' },
    move: 'Move my appointment', cancel: 'Cancel my appointment', pick: 'Choose a new time', paris: 'Times shown in Paris time',
    confirmMove: 'Confirm the new time', back: 'Back', confirmCancel: 'Do you really want to cancel this appointment?',
    moved: 'Your appointment has been moved. A confirmation email is on its way.', cancelled: 'Your appointment has been cancelled.',
    taken: 'This time slot has just been booked. Please choose another.', error: 'An error occurred. Please try again.',
    notFound: 'This appointment cannot be found or has already been cancelled.', past: 'This appointment has already taken place.', newBooking: 'Book a new conversation',
    doneCancel: 'Appointment cancelled. We would be delighted to speak with you whenever suits you.', at: 'at', place: 'Place'
  }
};

const humanDate = (iso, lang) => new Date(`${iso}T12:00:00`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const Shell = ({ children }) => (
  <section style={{ minHeight: '100vh', padding: '80px 24px', background: 'var(--bg-primary)' }}>
    <div style={{ maxWidth: '720px', margin: '0 auto' }}>
      <p className="meta-text" style={{ letterSpacing: '4px', marginBottom: '48px' }}>FENICIS</p>
      {children}
    </div>
  </section>
);

const SlotPicker = ({ lang, t, onConfirm, onBack, busy, month }) => {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [slots, setSlots] = useState({ times: [], taken: [] });
  const [blocked, setBlocked] = useState([]);
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });

  useEffect(() => { fetch(`${API}/bookings/blocked-days`).then((r) => r.json()).then(setBlocked).catch(() => {}); }, []);
  useEffect(() => {
    if (!date) return;
    fetch(`${API}/bookings/availability?date=${toIso(date)}`).then((r) => r.json()).then(setSlots).catch(() => {});
  }, [date]);

  return (
    <div data-testid="manage-slot-picker">
      <h2 className="heading-3" style={{ marginBottom: '24px' }}>{t.pick}</h2>
      <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
        <Calendar data-testid="manage-calendar" mode="single" selected={date} onSelect={(d) => { setDate(d); setTime(null); }}
          defaultMonth={month} locale={lang === 'en' ? enGB : frLocale} weekStartsOn={1} className="rounded-md border"
          disabled={(d) => toIso(d) <= today || d.getDay() === 0 || d.getDay() === 6 || blocked.includes(toIso(d))} />
        {date && (
          <div>
            <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '16px' }}>{t.paris}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {slots.times.map((s) => {
                const taken = slots.taken.includes(s);
                return (
                  <button key={s} data-testid={`manage-time-${s.replace(':', '')}`} disabled={taken} onClick={() => setTime(s)}
                    style={{ padding: '12px 20px', fontSize: '14px', border: '1px solid var(--border-light)', cursor: taken ? 'not-allowed' : 'pointer', opacity: taken ? 0.35 : 1, textDecoration: taken ? 'line-through' : 'none',
                      background: time === s ? 'var(--interactive-base)' : 'var(--bg-primary)', color: time === s ? 'var(--bg-primary)' : 'var(--text-primary)' }}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '16px', marginTop: '32px', flexWrap: 'wrap' }}>
        <button data-testid="manage-confirm-move-button" className="btn-primary" disabled={!date || !time || busy} onClick={() => onConfirm(toIso(date), time, () => setTime(null))}>{t.confirmMove}</button>
        <button data-testid="manage-back-button" className="btn-secondary" onClick={onBack}>{t.back}</button>
      </div>
    </div>
  );
};

export default function ManageBooking({ token }) {
  const [state, setState] = useState({ status: 'loading' });
  const [moving, setMoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const browserLang = typeof navigator !== 'undefined' && navigator.language?.startsWith('fr') ? 'fr' : 'en';

  useEffect(() => {
    fetch(`${API}/manage/${token}`).then(async (r) => {
      if (r.ok) return setState({ status: 'ready', booking: await r.json() });
      setState({ status: r.status === 410 ? 'past' : 'notFound' });
    }).catch(() => setState({ status: 'notFound' }));
  }, [token]);

  const b = state.booking;
  const lang = b?.language === 'en' ? 'en' : b ? 'fr' : browserLang;
  const t = TXT[lang];
  const contactHref = lang === 'en' ? '/en/contact' : '/fr/contact';

  const reschedule = async (date, time, resetTime) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/manage/${token}/reschedule`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date, time }) });
      if (r.status === 409) { toast.error(t.taken); resetTime(); return; }
      if (!r.ok) throw new Error('failed');
      setState({ status: 'ready', booking: { ...b, date, time } });
      setMoving(false);
      toast.success(t.moved);
    } catch { toast.error(t.error); } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm(t.confirmCancel)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/manage/${token}/cancel`, { method: 'POST' });
      if (!r.ok) throw new Error('failed');
      setState({ status: 'cancelled', booking: b });
      toast.success(t.cancelled);
    } catch { toast.error(t.error); } finally { setBusy(false); }
  };

  if (state.status === 'loading') return <Shell><p className="body-regular">…</p></Shell>;
  if (state.status !== 'ready') {
    const msg = { notFound: t.notFound, past: t.past, cancelled: t.doneCancel }[state.status];
    return (
      <Shell>
        <p data-testid="manage-status-message" className="heading-3" style={{ marginBottom: '32px' }}>{msg}</p>
        <a data-testid="manage-new-booking-link" href={contactHref} className="btn-primary">{t.newBooking}</a>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="manage-booking-page">
        <h1 className="heading-2" style={{ marginBottom: '8px' }}>{t.title}</h1>
        <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>{t.with}</p>
        <div style={{ borderTop: '1px solid var(--border-light)', borderBottom: '1px solid var(--border-light)', padding: '24px 0', marginBottom: '40px' }}>
          <p data-testid="manage-booking-when" className="heading-3" style={{ marginBottom: '8px' }}>{(() => { const s = `${humanDate(b.date, lang)} ${t.at} ${b.time}`; return s.charAt(0).toUpperCase() + s.slice(1); })()}</p>
          <p data-testid="manage-booking-format" className="body-regular">{t.formats[b.format]} · 30 min</p>
          {b.location && <p className="body-regular" style={{ marginTop: '8px' }}>{t.place}{lang === 'en' ? ':' : '\u00a0:'} {b.location}</p>}
        </div>
        {moving ? (
          <SlotPicker lang={lang} t={t} busy={busy} month={new Date(`${b.date}T12:00:00`)} onConfirm={reschedule} onBack={() => setMoving(false)} />
        ) : (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <button data-testid="manage-move-button" className="btn-primary" onClick={() => setMoving(true)}>{t.move}</button>
            <button data-testid="manage-cancel-button" className="btn-secondary" disabled={busy} onClick={cancel}>{t.cancel}</button>
          </div>
        )}
      </div>
    </Shell>
  );
}
